<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-3xl">
      <div class="mb-5 flex min-w-0 items-start gap-3">
        <HistoryBackButton />
        <div class="min-w-0">
          <h1 class="m-0 text-2xl font-semibold text-slate-950 sm:text-3xl">Settings</h1>
          <p class="mt-2 text-sm font-medium text-slate-500">Manage account access, support, and app links.</p>
        </div>
      </div>

      <div class="overflow-hidden rounded-[1.1rem] border border-blue-100 bg-white shadow-sm">
        <button
          v-for="item in settingsLinks"
          :key="item.label"
          type="button"
          class="flex min-h-[4.25rem] w-full items-center gap-4 border-b border-blue-100 px-5 text-left text-[1.02rem] font-semibold text-slate-700 transition last:border-b-0 hover:bg-blue-50/40"
          @click="handleSetting(item)"
        >
          <i class="text-2xl text-blue-600" :class="item.icon" aria-hidden="true"></i>
          <span>{{ item.label }}</span>
        </button>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="inviteModalOpen"
        class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
        role="dialog"
        aria-modal="true"
        aria-label="Invite friends"
        @click.self="closeInviteModal"
      >
        <section class="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
          <header class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div class="min-w-0">
              <h2 class="m-0 text-lg font-semibold text-slate-950">Invite friends</h2>
              <p class="m-0 mt-1 text-xs font-medium text-slate-500">Share NetFlyr with your contacts.</p>
            </div>
            <button
              class="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950"
              type="button"
              aria-label="Close invite friends"
              @click="closeInviteModal"
            >
              <i class="ri-close-line text-xl" aria-hidden="true"></i>
            </button>
          </header>

          <div class="grid gap-4 p-4">
            <div v-if="inviteLoading" class="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700">
              Loading invite details...
            </div>
            <div v-else-if="inviteError" class="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700">
              {{ inviteError }}
            </div>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Invite response</span>
              <textarea
                class="min-h-28 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-normal leading-6 text-slate-800 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                :value="inviteDisplayText"
                readonly
              />
            </label>

            <div class="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
              <input
                class="min-h-10 min-w-0 flex-1 truncate border-0 bg-transparent px-2 text-sm font-normal text-slate-700 outline-none"
                :value="inviteShareUrl"
                readonly
              />
              <button
                class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700"
                type="button"
                aria-label="Copy invite"
                @click="copyInvite"
              >
                <i class="ri-file-copy-line text-lg" aria-hidden="true"></i>
              </button>
            </div>

            <div class="grid grid-cols-4 gap-2">
              <button
                class="grid min-h-12 place-items-center rounded-2xl border border-slate-200 bg-white text-xl text-blue-600 transition hover:border-blue-200 hover:bg-blue-50"
                type="button"
                aria-label="Copy invite"
                @click="copyInvite"
              >
                <i class="ri-file-copy-line" aria-hidden="true"></i>
              </button>
              <a
                v-for="item in inviteShareLinks"
                :key="item.label"
                class="grid min-h-12 place-items-center rounded-2xl border border-slate-200 bg-white text-xl transition hover:border-blue-200 hover:bg-blue-50"
                :class="item.class"
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="item.label"
              >
                <i :class="item.icon" aria-hidden="true"></i>
              </a>
            </div>

            <p v-if="inviteCopied" class="m-0 text-center text-xs font-medium text-emerald-700">
              Invite copied.
            </p>
          </div>
        </section>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="appsModalOpen"
        class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
        role="dialog"
        aria-modal="true"
        aria-label="More Amplifier apps"
        @click.self="closeAppsModal"
      >
        <section class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <header class="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
            <div class="min-w-0">
              <h2 class="m-0 text-lg font-semibold text-slate-950">More Amplifier Apps</h2>
              <p class="m-0 mt-1 text-sm font-normal leading-5 text-slate-500">Browse Amplifier apps and open the right store for your device.</p>
            </div>
            <button
              class="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950"
              type="button"
              aria-label="Close more apps"
              @click="closeAppsModal"
            >
              <i class="ri-close-line text-xl" aria-hidden="true"></i>
            </button>
          </header>

          <div class="min-h-0 overflow-y-auto p-3 sm:p-4">
            <div v-if="appsLoading" class="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700">
              Loading apps...
            </div>
            <div v-else-if="appsError" class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {{ appsError }}
            </div>
            <div v-else-if="!amplifierApps.length" class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm font-medium text-slate-500">
              No apps available right now.
            </div>
            <div v-else class="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <article
                v-for="app in amplifierApps"
                :key="app.id"
                class="grid gap-3 border-b border-slate-200 p-3 last:border-b-0 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:p-4"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <img
                    v-if="app.image"
                    class="h-14 w-14 shrink-0 rounded-2xl bg-slate-100 object-cover"
                    :src="app.image"
                    :alt="app.name"
                    loading="lazy"
                  />
                  <span v-else class="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-blue-50 text-lg font-semibold text-blue-700">
                    {{ app.initial }}
                  </span>
                  <div class="min-w-0 sm:hidden">
                    <h3 class="m-0 truncate text-base font-semibold text-slate-950">{{ app.name }}</h3>
                    <span class="mt-1 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-blue-700">
                      {{ app.typeLabel }}
                    </span>
                  </div>
                </div>

                <div class="min-w-0">
                  <div class="hidden min-w-0 items-center gap-2 sm:flex">
                    <h3 class="m-0 truncate text-base font-semibold text-slate-950">{{ app.name }}</h3>
                    <span class="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-blue-700">
                      {{ app.typeLabel }}
                    </span>
                  </div>
                  <p v-if="app.description" class="m-0 mt-1 line-clamp-2 text-sm font-normal leading-6 text-slate-500">
                    {{ app.description }}
                  </p>
                </div>

                <div class="flex flex-wrap gap-2 sm:justify-end">
                  <a
                    v-if="app.iosUrl"
                    class="nf-primary-button nf-primary-button--compact flex-1 sm:flex-none"
                    :href="app.iosUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i class="ri-apple-fill text-base" aria-hidden="true"></i>
                    iOS
                  </a>
                  <a
                    v-if="app.androidUrl"
                    class="nf-primary-button nf-primary-button--compact flex-1 sm:flex-none"
                    :href="app.androidUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i class="ri-android-fill text-base" aria-hidden="true"></i>
                    Android
                  </a>
                </div>
              </article>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'

const router = useRouter()
const session = useSessionStore()
const inviteModalOpen = ref(false)
const inviteLoading = ref(false)
const inviteError = ref('')
const inviteCopied = ref(false)
const inviteResponseText = ref('')
const inviteResponseUrl = ref('')
const appsModalOpen = ref(false)
const appsLoading = ref(false)
const appsError = ref('')
const amplifierApps = ref([])

const fallbackInviteUrl = computed(() => (typeof window === 'undefined' ? '/' : window.location.origin))
const inviteUrlFromText = computed(() => inviteResponseText.value.match(/https?:\/\/[^\s]+/i)?.[0] || '')
const inviteShareUrl = computed(() => inviteResponseUrl.value || inviteUrlFromText.value || fallbackInviteUrl.value)
const inviteDisplayText = computed(() => inviteResponseText.value || `Join NetFlyr and explore local flyers: ${inviteShareUrl.value}`)
const encodedInviteText = computed(() => encodeURIComponent(inviteDisplayText.value))
const encodedInviteUrl = computed(() => encodeURIComponent(inviteShareUrl.value))
const inviteShareLinks = computed(() => [
  {
    label: 'Share on Facebook',
    icon: 'ri-facebook-fill',
    class: 'text-blue-700',
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodedInviteUrl.value}`
  },
  {
    label: 'Share on X',
    icon: 'ri-twitter-x-line',
    class: 'text-slate-950',
    href: `https://twitter.com/intent/tweet?text=${encodedInviteText.value}&url=${encodedInviteUrl.value}`
  },
  {
    label: 'Share on WhatsApp',
    icon: 'ri-whatsapp-line',
    class: 'text-emerald-600',
    href: `https://wa.me/?text=${encodedInviteText.value}`
  }
])

const settingsLinks = [
  { label: 'Change Password', icon: 'ri-lock-password-line', to: '/account/change-password' },
  { label: 'Timezone', icon: 'ri-time-zone-line', to: '/settings/timezone' },
  { label: 'Invite Friends', icon: 'ri-user-add-line', action: 'invite' },
  { label: 'Support', icon: 'ri-chat-1-line', to: '/support' },
  { label: 'Terms & Conditions', icon: 'ri-file-text-line', to: '/terms-conditions' },
  { label: 'Privacy Policy', icon: 'ri-shield-check-line', to: '/privacy-policy' },
  { label: 'About NetFlyr', icon: 'ri-information-line', to: '/about' },
  { label: 'More Amplifier Apps', icon: 'ri-apps-2-line', action: 'apps' },
  { label: 'Logout', icon: 'ri-logout-circle-r-line', action: 'logout' },
  { label: 'Delete Account', icon: 'ri-logout-circle-line', action: 'delete' }
]

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '')

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  return [
    payload.data,
    payload.list,
    payload.result,
    payload.apps,
    payload.data?.list,
    payload.data?.data,
    payload.data?.apps,
    payload.result?.list
  ].find(Array.isArray) || []
}

const buildImageUrl = (value) => {
  const image = String(value || '').trim()
  if (!image) return ''
  if (/^https?:\/\//i.test(image)) return image
  return `https://amplifierapp.xyz/${image.replace(/^\/+/, '')}`
}

const normalizeStoreUrl = (value) => {
  const url = String(value || '').trim()
  if (!url) return ''
  return url.replace(/^https:\/(?!\/)/i, 'https://').replace(/^http:\/(?!\/)/i, 'http://')
}

const normalizeApp = (item = {}, index = 0) => {
  const name = String(firstValue(item.app_name, item.name, item.title, item.app_title, `Amplifier App ${index + 1}`)).trim()
  const type = String(firstValue(item.type, item.app_type, '')).trim()
  return {
    id: String(firstValue(item.id, item.app_id, item.slug, name, index)),
    name,
    type,
    typeLabel: type || 'App',
    initial: (name.charAt(0) || 'A').toUpperCase(),
    description: String(firstValue(item.sub_title, item.description, item.desc, item.app_description, item.short_description, '')).trim(),
    image: buildImageUrl(firstValue(item.image, item.app_icon, item.icon, item.logo, item.app_image, '')),
    iosUrl: normalizeStoreUrl(firstValue(item.url_ios, item.ios, item.ios_link, item.ios_url, item.app_store_url, item.appstore_url, item.apple_url, '')),
    androidUrl: normalizeStoreUrl(firstValue(item.url_android, item.android, item.android_link, item.android_url, item.play_store_url, item.playstore_url, item.google_play_url, ''))
  }
}

const extractInviteDetails = (payload = {}) => {
  const source = Array.isArray(payload?.data) ? payload.data[0] || {} : payload?.data || payload || {}
  const message = firstValue(
    source.netflyr_social_link,
    source.ivnite_friends,
    source.ivnite_friend,
    source.ivnite_friends_text,
    source.ivnite_friends_message,
    source.invite_friends,
    source.invite_friend,
    source.invite_friends_text,
    source.invite_friends_message,
    source.invite_text,
    source.invite_message,
    ''
  )
  const link = firstValue(
    source.netflyr_link,
    source.netflyr_url,
    source.ivnite_friends_link,
    source.ivnite_link,
    source.invite_friends_link,
    source.invite_friends_url,
    source.invite_link,
    source.invite_url,
    source.referral_link,
    source.referral_url,
    source.share_url,
    source.url,
    ''
  )

  return {
    text: String(message || '').trim(),
    url: String(link || '').trim()
  }
}

const loadInviteDetails = async () => {
  inviteLoading.value = true
  inviteError.value = ''
  inviteCopied.value = false

  try {
    const profile = session.profile || await session.fetchProfile()
    const details = extractInviteDetails(profile || session.user || {})
    inviteResponseText.value = details.text || `Join NetFlyr and explore local flyers: ${fallbackInviteUrl.value}`
    inviteResponseUrl.value = details.url
  } catch (error) {
    inviteResponseText.value = `Join NetFlyr and explore local flyers: ${fallbackInviteUrl.value}`
    inviteResponseUrl.value = fallbackInviteUrl.value
    inviteError.value = error?.response?.data?.message || error?.message || 'Profile invite text could not be loaded. Using the app link instead.'
  } finally {
    inviteLoading.value = false
  }
}

const openInviteModal = () => {
  inviteModalOpen.value = true
  loadInviteDetails()
}

const closeInviteModal = () => {
  inviteModalOpen.value = false
}

const copyInvite = async () => {
  try {
    await navigator.clipboard.writeText(inviteDisplayText.value)
    inviteCopied.value = true
    window.setTimeout(() => {
      inviteCopied.value = false
    }, 1800)
  } catch {
    inviteError.value = 'Invite could not be copied.'
  }
}

const loadAmplifierApps = async () => {
  appsLoading.value = true
  appsError.value = ''

  try {
    const { data } = await api.liveAppsInviteApp({ type: 'user' })
    amplifierApps.value = extractRows(data).map(normalizeApp)
  } catch (error) {
    amplifierApps.value = []
    appsError.value = error?.response?.data?.message || error?.message || 'Apps could not be loaded.'
  } finally {
    appsLoading.value = false
  }
}

const openAppsModal = () => {
  appsModalOpen.value = true
  loadAmplifierApps()
}

const closeAppsModal = () => {
  appsModalOpen.value = false
}

const handleSetting = async (item) => {
  if (item.to) {
    router.push(item.to)
    return
  }

  if (item.action === 'invite') {
    openInviteModal()
    return
  }

  if (item.action === 'apps') {
    openAppsModal()
    return
  }

  if (item.action === 'logout') {
    session.logout()
    router.push('/login')
    return
  }

  if (item.action === 'delete') {
    if (!session.userId) return
    const confirmed = window.confirm('Delete this account permanently?')
    if (!confirmed) return

    try {
      await api.deleteAccount({ id: session.userId })
    } catch {
      // Keep local logout behavior even if backend deletion fails here.
    } finally {
      session.logout()
      router.push('/register')
    }
  }
}
</script>
